import { useEffect, useState } from 'react';
import { type SendToIntegrationContainerProps } from './useSendToIntegrationLogic';
import {
  getEnterpriseById,
  getEnterpriseMemberById
} from '../utils/clients/walnutApi';
import { handleAsyncState } from '../utils/handleAsyncState';

export const useGetAllowedIntegrations = (
  sendToIntegrationProps: SendToIntegrationContainerProps
) => {
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationsError, setIntegrationsError] = useState<Error | null>(
    null
  );

  const supportedIntegrationSlugs =
    sendToIntegrationProps.supportedIntegrationSlugs
      ? sendToIntegrationProps.supportedIntegrationSlugs
      : [`jira-direct-cloud-integration`, `jira-on-prem-integration`];

  const [loadedSubscriptionsSlugs, setLoadedSubscriptionsSlugs] = useState<
    string[]
  >([]);

  const [allowedIntegrations, setAllowedIntegrations] = useState<string[]>([]);

  useEffect(() => {
    if (
      !sendToIntegrationProps.userEnterpriseId ||
      !sendToIntegrationProps.userId
    ) {
      return;
    }

    async function loadSubscriptionsSlags() {
      if (sendToIntegrationProps.subscriptionsFn) {
        const subscriptionsSlugs =
          await sendToIntegrationProps.subscriptionsFn();

        setLoadedSubscriptionsSlugs(subscriptionsSlugs);
        setAllowedIntegrations(
          supportedIntegrationSlugs.filter(supportedIntegrationSlug =>
            subscriptionsSlugs.includes(supportedIntegrationSlug)
          )
        );
        return;
      }

      const bearerToken = await sendToIntegrationProps.authFn();
      // load subscriptions from walnut
      const enterprise = await getEnterpriseById({
        apiHostname: sendToIntegrationProps.apiHostname,
        token: bearerToken,
        enterpriseId: sendToIntegrationProps.userEnterpriseId
      });

      const enterpriseMembers = await getEnterpriseMemberById({
        apiHostname: sendToIntegrationProps.apiHostname,
        token: bearerToken,
        enterpriseId: sendToIntegrationProps.userEnterpriseId
      });

      const productAccess = enterpriseMembers.find(
        ({ user_id }) => user_id === sendToIntegrationProps.userId
      )?.product_access;

      // filter loaded subscription to paid
      const loadedSubscriptions = enterprise.subscriptions
        .filter(
          subscription =>
            subscription.purchase_state === 'paid' &&
            productAccess?.some(
              ({ product_slug }) => product_slug === subscription.product_slug
            )
        )
        .map(subscription => subscription.product_slug);

      setLoadedSubscriptionsSlugs(loadedSubscriptions);
      setAllowedIntegrations(
        supportedIntegrationSlugs.filter(supportedIntegrationSlug =>
          loadedSubscriptions.includes(supportedIntegrationSlug)
        )
      );
    }

    handleAsyncState(
      loadSubscriptionsSlags,
      setIntegrationsLoading,
      setIntegrationsError
    );
  }, [sendToIntegrationProps.userEnterpriseId]);

  return {
    allowedIntegrations,
    loadedSubscriptionsSlugs,
    integrationsLoading,
    integrationsError
  };
};
